/**
 * <CountryFilterContext> — global "all/single country" filter for the
 * Admin Dashboard.
 *
 * Why a React Context?
 *   The dashboard renders ~10 sibling widgets (Top Dealer, Orphan
 *   Customers, Wallet Adoption, Customer 360, Mismatch Detection, …)
 *   that all want to know "is the admin currently focused on a single
 *   country?" Lifting the state into one shared store lets us:
 *     1. Persist the selection across page reloads via localStorage.
 *     2. Let each widget react independently (refetch its own endpoint
 *        with ``?country=XX`` instead of bubbling props through 4 levels
 *        of layout).
 *     3. Render a single header dropdown that drives everything.
 *
 * Source-of-truth priority (highest wins):
 *   1. ``user.preferred_country`` — persisted server-side per admin user.
 *      Mara logs in from a new laptop → her saved "IT" preference
 *      auto-applies, no manual re-click required.
 *   2. ``localStorage.lojack_admin_country_filter`` — last value chosen
 *      on THIS device, useful before /auth/me resolves and as offline
 *      fallback.
 *   3. ``"ALL"`` — no filter applied.
 *
 * Writes go to BOTH stores: localStorage (fast, immediate) and the
 * server (slow, fire-and-forget via PATCH /api/auth/me/preferences).
 * If the server save fails (transient 5xx, offline) the local change
 * still takes effect — the next successful save will resync.
 */

import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API } from "../config";

const STORAGE_KEY = "lojack_admin_country_filter";
const ALL = "ALL";

const CountryFilterContext = createContext({
  country: ALL,
  setCountry: () => {},
  isFiltered: false,
  ALL,
});

export const CountryFilterProvider = ({ children }) => {
  const { user } = useAuth();
  // Track which user we've already hydrated from so we don't keep
  // overwriting the local state every time React re-renders (which
  // would un-do the user's own manual ALL reset because the cached
  // ``user.preferred_country`` in AuthContext is stale until the next
  // /auth/me refresh).
  const hydratedForRef = useRef(null);

  const [country, setCountryState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved || ALL;
    } catch {
      return ALL;
    }
  });

  // Hydrate from server-side preference EXACTLY ONCE per logged-in
  // user. The trigger key is the admin email — every fresh login or
  // user switch re-runs the hydration. Subsequent state changes
  // (admin picks a new country via the dropdown) update the server
  // via PATCH but DO NOT bounce back through this effect, because
  // ``user.preferred_country`` from AuthContext only refreshes on
  // logout+login (cached in /auth/me response, not invalidated).
  useEffect(() => {
    if (!user || hydratedForRef.current === user.email) return;
    hydratedForRef.current = user.email;
    const serverPref = user.preferred_country;
    if (serverPref) {
      setCountryState(serverPref);
    } else {
      // Server says "no preference". Don't touch the current local
      // value if it came from this device's localStorage — that's a
      // legitimate pre-auth choice the user just made. Only reset if
      // both stores are empty (fresh session, new device).
      try {
        if (!localStorage.getItem(STORAGE_KEY)) setCountryState(ALL);
      } catch { /* ignore */ }
    }
  }, [user]);

  // Persist any change to localStorage so the choice survives reloads
  // and tab navigations within the admin section.
  useEffect(() => {
    try {
      if (country && country !== ALL) {
        localStorage.setItem(STORAGE_KEY, country);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // localStorage may be unavailable in private mode — ignore
    }
  }, [country]);

  const setCountry = useCallback((newCountry) => {
    const normalized = newCountry || ALL;
    setCountryState(normalized);

    // Server-side persistence — fire-and-forget. We do NOT await this
    // promise because the UI should react instantly; if the PATCH
    // fails the next change will retry.
    if (user) {
      const payload = { preferred_country: normalized === ALL ? null : normalized };
      axios.patch(`${API}/auth/me/preferences`, payload).catch((err) => {
        console.warn("[CountryFilter] server-side preference save failed", err?.message);
      });
    }
  }, [user]);

  const value = useMemo(
    () => ({ country, setCountry, isFiltered: country !== ALL, ALL }),
    [country, setCountry],
  );

  return (
    <CountryFilterContext.Provider value={value}>
      {children}
    </CountryFilterContext.Provider>
  );
};

/**
 * Hook for widgets to read the current filter.
 *
 * Returns ``{ country, setCountry, isFiltered, ALL }``.
 *
 * Widget pattern:
 *   const { country, isFiltered } = useCountryFilter();
 *   const params = isFiltered ? { country } : {};
 *   axios.get(`${API}/dashboard/foo`, { params });
 *
 * When ``isFiltered`` is false, callers should NOT send the ``country``
 * param at all so the backend can short-circuit the "no filter" branch.
 */
export const useCountryFilter = () => useContext(CountryFilterContext);
